import React from 'react';
import _ from 'lodash';
import { layout } from 'styled-system';
import styled, { css } from 'styled-components';

import { getProductWeight } from 'shared/helpers/products';
import { useViewport } from 'contexts/viewport';
import { Clickable } from 'shared/components';

export default function OptionsList({
  disabled,
  discountDisplay,
  onOptionSelect,
  options,
  selectedOption,
  showWeightSpecials,
  weightedSpecialPrices,
}) {
  const { width: viewportWidth } = useViewport();
  const atleastOneWeightHasDiscount = _.some(weightedSpecialPrices, ({ discountPercentage }) => discountPercentage > 0);

  return (
    <Container width={viewportWidth} hasDiscount={atleastOneWeightHasDiscount}>
      {_.map(options, (option) => {
        const selected = option.value === selectedOption.value;
        const weight = getProductWeight(option.value);
        const price = _.find(weightedSpecialPrices, [`weight`, weight]);
        const discountLabel = showWeightSpecials ? discountDisplay(price.discountPercentage) : null;
        const shouldShowStandardPrice =
          price && +price.standardPrice !== 0.0 && +price.price < +price.standardPrice && !!discountLabel;

        function handleClick() {
          onOptionSelect(option);
        }

        return (
          <ButtonContainer width={viewportWidth} key={option.value} hasDiscount={shouldShowStandardPrice}>
            <Button
              aria-checked={selected}
              disabled={disabled}
              hasDiscount={shouldShowStandardPrice}
              key={option.label}
              noBottomBorderRadius={!!discountLabel}
              onClick={handleClick}
              role='radio'
              selected={selected}
            >
              <span>{option.label}</span>
              {shouldShowStandardPrice && <StrikedOutPrice>${price.standardPrice}</StrikedOutPrice>}
              <BoldSpan hasDiscount={shouldShowStandardPrice}>{`$${Number(option.price).toFixed(2)}`}</BoldSpan>
              {discountLabel && <DiscountLabel selected={!disabled && selected}>{discountLabel}</DiscountLabel>}
            </Button>
          </ButtonContainer>
        );
      })}
    </Container>
  );
}

/* eslint-disable i18next/no-literal-string */
const DiscountLabel = styled.div`
  background: ${({ selected, theme }) => (selected ? theme.colors.primaryBlue : theme.colors.red[60])} !important;
  color: #fff;
  font-size: 11px;
  line-height: 11px;
  text-align: center;
  font-weight: bold;
  border-radius: 0px 0px 9px 9px;
  position: relative;
  bottom: -10px;
  padding: 2px 0;
  width: inherit;
  margin-top: -13px;
  top: 19px;
`;

const selectedStyles = css`
  border: ${({ theme }) => `2px solid ${theme.colors.primaryBlue}`};
  color: ${({ theme }) => theme.colors.primaryBlue};
`;

const ButtonContainer = styled.div`
  padding: 4.5px;
  &:first-child {
    padding-left: 0;
  }
  &:last-child {
    padding-right: 20px;
  }
`;

const Button = styled(Clickable)`
  align-items: center;
  border-radius: 10px;
  border: ${({ theme }) => `1px solid ${theme.colors.blueGrey[80]}`};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.grey[70] : theme.colors.primaryBlack)};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: ${({ disabled }) => (disabled ? `default` : `pointer`)};
  display: flex;
  flex-direction: column;
  font-size: 13px;
  height: ${({ hasDiscount }) => (hasDiscount ? `62px` : `59px`)};
  justify-content: center;
  padding: 0;
  width: 73px;
  min-width: 73px;
  line-height: ${({ hasDiscount }) => (hasDiscount ? `1` : `1.15`)};
  ${({ disabled, selected }) => !disabled && selected && selectedStyles};
  ${({ noBottomBorderRadius }) =>
    noBottomBorderRadius &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
`;

const StrikedOutPrice = styled.span`
  font-size: 12px;
  margin-top: 2.5px;
  color: ${({ theme }) => theme.colors.grey[70]};
  text-decoration-line: line-through;
`;

const BoldSpan = styled.span`
  font-weight: bold;
  font-size: 14px;
  margin-top: ${({ hasDiscount }) => (hasDiscount ? `2.5px` : `5px`)};
  margin-right: 0;
`;

const Container = styled.div`
  margin-bottom: 20px;
  margin-left: -20px;
  padding-left: 20px;
  height: ${({ hasDiscount }) => (hasDiscount ? `83px` : `75px`)};
  display: flex;
  overflow-x: auto;
  margin-top: 15px;

  ::-webkit-scrollbar {
    display: none;
  }

  ${layout}

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-top: 18px;
  }
`;
